import { guessColumnMap, parseCSVTable } from "../../../utils/fileUtils";
import {
  describeCarrierConflict,
  findCarrierConflict,
  normalizeCarrierIdentifiers,
  validateCarrierIdentifier,
} from "../../../utils/carrierIdentity";

/**
 * The spreadsheet-to-carrier mapping, and the checks a mapped row has to pass
 * before it becomes a carrier.
 *
 * A carrier record is four fields wide (businessName, email, phone, and a USDOT
 * number), so the import's real work is not the shape of the CSV but deciding
 * which rows are already in the yard's book — the duplicate rules live in
 * utils/carrierIdentity and are the same ones both create forms run. Kept pure
 * and free of AppSync so the row decisions can be tested without a network.
 */

// Column mapping labels. Business Name and Email are required: the name is the
// record, and the address is what the invite goes to — an imported carrier with
// no address is a carrier nobody can log in as.
// USDOT alone of the identifier schemes: a spreadsheet of carriers carries a
// DOT number or nothing, and one column per authority would be six mostly-empty
// dropdowns. Every other scheme is added on the carrier afterwards, by the yard
// or by the carrier itself.
export const CARRIER_IMPORT_FIELDS = [
  { id: "businessName", name: "Business Name", required: true },
  { id: "email", name: "Email", required: true },
  { id: "phone", name: "Phone" },
  { id: "usdot", name: "USDOT Number" },
];

export const REQUIRED_CARRIER_IMPORT_FIELDS = CARRIER_IMPORT_FIELDS.filter(
  (field) => field.required,
);

// Deliberately loose — enough to catch a mis-mapped column (a phone number or a
// business name landing in Email), not to adjudicate what an address may hold.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Turn a carrier CSV file's text into header-keyed rows.
 *
 * The parsing itself is shared with the other column-mapping imports; this name
 * is what the carrier dialog and its tests read.
 *
 * @param {string} text - the file contents
 * @returns {{columns: {id: string, name: string}[], rows: object[], errors: string[]}}
 */
export const parseCarrierCSV = parseCSVTable;

const cellValue = (row, column) =>
  column ? String(row?.[column] ?? "").trim() : "";

/**
 * The carrier a mapped row describes.
 *
 * The USDOT column becomes an identifier row, so the duplicate check below sees
 * it the same way it sees a number typed into a form — which matters most here:
 * an imported file is exactly where the same carrier arrives twice under two
 * spellings of its name.
 *
 * @param {object} row - a parseCarrierCSV row
 * @param {object} fieldMap - {[fieldId]: csvColumn}
 * @returns {{businessName: string, email: string, phone: string,
 *   identifiers: object[]}}
 */
export const mapCarrierRow = (row, fieldMap) => {
  const usdot = cellValue(row, fieldMap?.usdot);

  return {
    businessName: cellValue(row, fieldMap?.businessName),
    email: cellValue(row, fieldMap?.email).toLowerCase(),
    phone: cellValue(row, fieldMap?.phone),
    // Normalised here rather than at write time so the plan, the preview and the
    // created record all agree on the number.
    identifiers: normalizeCarrierIdentifiers(
      usdot ? [{ scheme: "USDOT", value: usdot }] : [],
    ),
  };
};

/**
 * Decide every row: which carriers to create, and why the rest are being left.
 *
 * A row is skipped rather than fixed. Guessing at a blank business name or
 * merging a row onto the carrier it collides with would write records the
 * manager never reviewed; naming the row and its reason lets them correct the
 * spreadsheet and re-upload, which is the loop the dialog is built around.
 *
 * Rows are checked against the carriers already accepted from this same file as
 * well as the yard's book, so a file that lists one carrier twice imports it
 * once.
 *
 * @param {object[]} rows - parseCarrierCSV rows
 * @param {object} fieldMap - {[fieldId]: csvColumn}
 * @param {object[]} existingCarriers - the yard's carriers, active and archived
 * @returns {{decisions: {rowIndex: number, label: string, values: object, reason: string|null}[],
 *   importable: object[], skipped: object[]}} one decision per row, plus the two
 *   views of it the dialog and the import loop each want.
 */
export const planCarrierImport = (rows, fieldMap, existingCarriers) => {
  // The book grows as rows are accepted, so the second copy of a carrier in the
  // file collides with the first rather than with nothing.
  const book = [...(existingCarriers ?? [])];

  const decide = (values) => {
    if (!values.businessName) return 'missing "Business Name"';
    if (!values.email) return 'missing "Email"';
    if (!EMAIL_SHAPE.test(values.email))
      return `"${values.email}" is not an email address`;
    // A mis-mapped column lands a phone number in USDOT. Skipping the row names
    // the file's problem; importing it would file the carrier under a number
    // that is not its own, which is worse than not importing it.
    const badIdentifier = (values.identifiers ?? [])
      .map((identifier) => validateCarrierIdentifier(identifier))
      .find(Boolean);
    if (badIdentifier) return badIdentifier;
    const conflict = findCarrierConflict(book, values);
    return conflict ? describeCarrierConflict(conflict) : null;
  };

  const decisions = (rows ?? []).map((row, index) => {
    const values = mapCarrierRow(row, fieldMap);
    const reason = decide(values);
    // An id is what makes an accepted row a candidate at all: findCarrierConflict
    // skips whatever matches the excludeId it was given, and an id-less entry
    // matches an absent one — which would let a file list the same carrier twice
    // and import it twice.
    if (!reason) book.push({ ...values, id: `csv-row-${index}` });
    return {
      rowIndex: row?.rowIndex ?? index,
      label: `Row ${index + 1}${
        values.businessName ? ` (${values.businessName})` : ""
      }`,
      values,
      reason,
    };
  });

  return {
    decisions,
    importable: decisions.filter((d) => !d.reason).map((d) => d.values),
    skipped: decisions.filter((d) => d.reason),
  };
};

// Header spellings seen in the exports yards actually arrive with. Matched on a
// letters-only reduction of the header, so "Business Name", "business_name" and
// "BUSINESSNAME" are one entry.
const HEADER_SYNONYMS = {
  businessName: [
    "businessname",
    "carrier",
    "carriername",
    "company",
    "companyname",
    "name",
  ],
  email: ["email", "emailaddress", "dispatchemail", "contactemail", "mail"],
  phone: ["phone", "phonenumber", "telephone", "tel", "dispatchphone", "mobile"],
  usdot: ["usdot", "usdotnumber", "usdotno", "dot", "dotnumber", "dotno"],
};

/**
 * A starting mapping, so a well-named export needs no dropdown work.
 *
 * @param {{id: string}[]} columns - parseCarrierCSV columns
 * @returns {object} {[fieldId]: csvColumn}
 */
export const guessFieldMap = (columns) =>
  guessColumnMap(CARRIER_IMPORT_FIELDS, HEADER_SYNONYMS, columns);

/**
 * Whether the mapping is complete enough to import at all.
 *
 * @param {object} fieldMap - {[fieldId]: csvColumn}
 * @returns {string[]} one message per unmapped required field
 */
export const missingRequiredMappings = (fieldMap) =>
  REQUIRED_CARRIER_IMPORT_FIELDS.filter((field) => !fieldMap?.[field.id]).map(
    (field) => `"${field.name}" mapping is required.`,
  );

/**
 * One notification for a finished import.
 *
 * @param {{created: number, invited: number, skipped: number, failed: number}}
 *   summary
 * @returns {string}
 */
export const describeImportSummary = ({ created, invited, skipped, failed }) => {
  const parts = [
    `${created} carrier${created === 1 ? "" : "s"} imported`,
    `${invited} invite${invited === 1 ? "" : "s"} sent`,
  ];
  if (skipped) parts.push(`${skipped} skipped`);
  if (failed) parts.push(`${failed} failed`);
  return `${parts.join(", ")}.`;
};
